import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";
import Skeleton from "react-loading-skeleton";

import { ContentChangeModal } from "@/components/home/modals/ContentChangeModal";
import { PageContainer } from "@/components/home/sections/PageContainer";
import ProfileCertificationsSection from "@/components/home/sections/ProfileCertificationsSection";
import ProfileEducationSection from "@/components/home/sections/ProfileEducationSection";
import ProfileEndorsementsSection from "@/components/home/sections/ProfileEndorsementsSection";
import ProfileIntroductionSection from "@/components/home/sections/ProfileIntroductionSection";
import ProfileKeySkillSetSection from "@/components/home/sections/ProfileKeySkillSetSection";
import ProfileProjectsSection from "@/components/home/sections/ProfileProjectsSection";
import ProfileRecognitionsSection from "@/components/home/sections/ProfileRecognitionsSection";
import { EditPageSection } from "@/components/home/sections/ProfileSectionCard";
import ProfileSectionsContainer from "@/components/home/sections/ProfileSectionsContainer";
import ProfileSkillsSection from "@/components/home/sections/ProfileSkillsSection";
import ProfileVolunteerSection from "@/components/home/sections/ProfileVolunteerSection";
import ProfileWorkExperienceSection from "@/components/home/sections/ProfileWorkExperienceSection";
import { SkeletonWrapper } from "@/components/onboarding/ui/Skeleton";
import { QuotaExceededModal } from "@/components/shared/QuotaExceededModal";
import type { CommonModels } from "@/data/common/common.models";
import type { ProfileModels } from "@/data/profile/profile.models";
import { ProjectsQueries } from "@/data/projects/projects.queries";
import { PublicProjectsQueries } from "@/data/publicProjects/publicProjects.queries";
import { useDemoAlternativeQuery } from "@/hooks/demo/useDemoAlternativeQuery";
import { useWorthDataChangeDetection } from "@/hooks/useWorthDataChangeDetection";
import { DemoContext } from "@/providers/demo/DemoContext";
import { useOverlayQueueStore } from "@/providers/overlay/overlay-queue.store";

interface IProps {
  userProfile?: ProfileModels.FullProfileResponseDto | null;
  handleEditSection?: (section: EditPageSection) => void;
  worthData?: CommonModels.WorthNumbersDto;
  settingUpProfile?: boolean;
}

const Profile = ({ userProfile, handleEditSection, settingUpProfile }: IProps) => {
  const { demoUserId } = DemoContext.useDemo();
  const { endorsements: endorsementsFlag } = useFlags();
  const { enqueueOverlay, dequeueOverlay, currentOverlay } = useOverlayQueueStore();

  // Projects - switch between private and public endpoints
  const { data: projects } = useDemoAlternativeQuery(
    { query: ProjectsQueries.useGet, params: [{ limit: 50 }], options: { refetchOnMount: "always", staleTime: 0 } },
    {
      query: PublicProjectsQueries.useGetProjects,
      params: [{ userId: demoUserId, limit: 50 }],
      options: {},
    },
  );

  // Featured project - switch between private and public endpoints
  const { data: featuredProject } = useDemoAlternativeQuery(
    { query: ProjectsQueries.useGetFeatured, options: { staleTime: 0, refetchOnMount: "always" } },
    { query: PublicProjectsQueries.useGetFeaturedProject, params: [{ userId: demoUserId }], options: {} },
  );

  const { triggerRecalculation } = useWorthDataChangeDetection();

  const profileData = userProfile?.dataV02;

  const skillsData = profileData?.person?.profile?.skills;

  const workExperienceData = profileData?.companies;

  const educationData = profileData?.education;

  const certificationData = profileData?.others?.licenses_or_certifications;
  const volunteerData = profileData?.others?.volunteer_experiences;
  const recognitionData = profileData?.others?.achievements_or_awards;

  const handleProjectSaved = useCallback(() => {
    enqueueOverlay({
      id: "recalculation-modal",
      type: "recalculation",
      priority: 1,
    });
  }, [enqueueOverlay]);

  const handleRecalculate = async () => {
    await triggerRecalculation();
    dequeueOverlay();
  };

  const ProfileIntroductionSectionComponent = useMemo(
    () => (
      <ProfileIntroductionSection
        userProfile={userProfile}
        userName={userProfile?.user.name || ""}
      />
    ),
    [userProfile],
  );
  const ProfileKeySkillSetSectionComponent = useMemo(
    () => (
      <ProfileKeySkillSetSection
        className="min-h-[440px] [&>div]:h-[440px]"
        onEditSectionClicked={handleEditSection ? () => handleEditSection(EditPageSection.Skills) : undefined}
        skills={skillsData}
        isAnalytics={false}
        hideLabel
      />
    ),
    [skillsData, handleEditSection],
  );

  const ProfileProjectsSectionComponent = useMemo(
    () => (
      <ProfileProjectsSection
        projects={(projects?.items as CommonModels.ProjectWithMediaDto[]) || []}
        featuredProject={featuredProject as CommonModels.ProjectWithMediaDto}
        onProjectSaved={handleProjectSaved}
      />
    ),
    [projects, featuredProject, handleProjectSaved],
  );

  const ProfileSkillsSectionComponent = useMemo(
    () => (
      <ProfileSkillsSection
        skills={skillsData}
        onEditSectionClicked={handleEditSection ? () => handleEditSection(EditPageSection.Skills) : undefined}
      />
    ),
    [skillsData, handleEditSection],
  );

  const ProfileEndorsementsSectionComponent = useMemo(
    () => (endorsementsFlag ? <ProfileEndorsementsSection /> : null),
    [endorsementsFlag],
  );

  const ProfileCertificationsSectionComponent = useMemo(
    () => (
      <ProfileCertificationsSection
        certifications={certificationData}
        onEditSectionClicked={handleEditSection ? () => handleEditSection(EditPageSection.Certifications) : undefined}
      />
    ),
    [certificationData, handleEditSection],
  );

  const ProfileVolunteerSectionComponent = useMemo(
    () => (
      <ProfileVolunteerSection
        volunteer={volunteerData}
        onEditSectionClicked={handleEditSection ? () => handleEditSection(EditPageSection.Volunteer) : undefined}
      />
    ),
    [volunteerData, handleEditSection],
  );

  const ProfileRecognitionsSectionComponent = useMemo(
    () => (
      <ProfileRecognitionsSection
        recognitions={recognitionData}
        onEditSectionClicked={handleEditSection ? () => handleEditSection(EditPageSection.Recognition) : undefined}
      />
    ),
    [recognitionData, handleEditSection],
  );

  const ProfileWorkExperienceSectionComponent = useMemo(
    () => (
      <ProfileWorkExperienceSection
        workExperience={workExperienceData}
        onEditSectionClicked={handleEditSection ? () => handleEditSection(EditPageSection.WorkExperience) : undefined}
      />
    ),
    [workExperienceData, handleEditSection],
  );

  const ProfileEducationSectionComponent = useMemo(
    () => (
      <ProfileEducationSection
        education={educationData}
        onEditSectionClicked={handleEditSection ? () => handleEditSection(EditPageSection.Education) : undefined}
      />
    ),
    [educationData, handleEditSection],
  );

  return (
    <>
      <PageContainer>
        <ProfileSectionsContainer>
          {settingUpProfile ? <SkeletonWrapper className="h-[342px]" /> : ProfileIntroductionSectionComponent}
        </ProfileSectionsContainer>
        <ProfileSectionsContainer>{ProfileEndorsementsSectionComponent}</ProfileSectionsContainer>

        <ProfileSectionsContainer>
          {settingUpProfile ? <Skeleton className="h-[744px] w-full rounded-lg" /> : ProfileProjectsSectionComponent}
        </ProfileSectionsContainer>

        <div className="mb-8 grid grid-cols-3 gap-2">
          <div className="flex flex-col gap-2">
            {settingUpProfile ? (
              <SkeletonWrapper containerClassName="flex min-h-[440px] max-h-[440px]" />
            ) : (
              ProfileKeySkillSetSectionComponent
            )}
            {settingUpProfile ? (
              <SkeletonWrapper containerClassName="flex min-h-[440px] max-h-[440px]" />
            ) : (
              ProfileSkillsSectionComponent
            )}
          </div>
          <div className="flex flex-col gap-2">
            {settingUpProfile ? (
              <SkeletonWrapper containerClassName="flex min-h-[640px] max-h-[640px]" />
            ) : (
              ProfileWorkExperienceSectionComponent
            )}
            {settingUpProfile ? (
              <SkeletonWrapper containerClassName="flex min-h-[440px] max-h-[440px]" />
            ) : (
              ProfileEducationSectionComponent
            )}
          </div>
          <div className="flex flex-col gap-2">
            {settingUpProfile ? (
              <SkeletonWrapper containerClassName="flex min-h-[440px] max-h-[440px]" />
            ) : (
              ProfileVolunteerSectionComponent
            )}
            {settingUpProfile ? (
              <SkeletonWrapper containerClassName="flex min-h-[640px] max-h-[640px]" />
            ) : (
              ProfileRecognitionsSectionComponent
            )}
            {settingUpProfile ? (
              <SkeletonWrapper containerClassName="flex min-h-[440px] max-h-[440px]" />
            ) : (
              ProfileCertificationsSectionComponent
            )}
          </div>
        </div>
      </PageContainer>

      <ContentChangeModal
        isOpen={currentOverlay?.type === "recalculation"}
        onClose={() => dequeueOverlay()}
        onContinueEditing={() => dequeueOverlay()}
        onRecalculate={handleRecalculate}
      />
      <QuotaExceededModal />
    </>
  );
};

export default Profile;
